import { IReplacementObject } from 'src/grammar-checker/models/api';
import { SelectionEndMenuTypes } from '.';
import { QuillEditorIDS } from '../../_common/handlers/quillEditor';
import { Suggestions, AutoSuggestModelName } from '../types/autosuggest';
import { OnboardingName } from '../types/onboarding';
import { ProjectState, ProjectStatusEnum } from '../types/project';
import { IBaseEditor } from './IBaseEditor';

export type PositonObj = {
  writeAssist: { top: number; left: number };
  tooltip: { top: number; left: number };
};

export enum BaseEditorModes {
  FULL,
  MINIMAL_HTML,
}

export enum EditorExecCommands {
  MCERESETDESIGNMODE = 'mceresetdesignmode',
  MCEBEGINUNDOLEVEL = 'mcebeginundolevel',
  MCEENDUNDOLEVEL = 'mceendundolevel',
  MCEADDUNDOLEVEL = 'mceaddundolevel',
  CUT = 'cut',
  COPY = 'copy',
  PASTE = 'paste',
  UNLINK = 'unlink',
  JUSTIFYLEFT = 'justifyleft',
  JUSTIFYCENTER = 'justifycenter',
  JUSTIFYRIGHT = 'justifyright',
  JUSTIFYFULL = 'justifyfull',
  JUSTIFYNONE = 'justifynone',
  INSERTUNORDEREDLIST = 'insertunorderedlist',
  INSERTORDEREDLIST = 'insertorderedlist',
  BOLD = 'bold',
  ITALIC = 'italic',
  UNDERLINE = 'underline',
  STRIKETHROUGH = 'strikethrough',
  SUPERSCRIPT = 'superscript',
  SUBSCRIPT = 'subscript',
  FORECOLOR = 'forecolor',
  HILITECOLOR = 'hilitecolor',
  FONTNAME = 'fontname',
  FONTSIZE = 'fontsize',
  LINEHEIGHT = 'lineheight',
  REMOVEFORMAT = 'removeformat',
  MCEBLOCKQUOTE = 'mceblockquote',
  FORMATBLOCK = 'formatblock',
  MCECLEANUP = 'mcecleanup',
  MCEREMOVENODE = 'mceremovenode',
  MCESELECTNODEDEPTH = 'mceselectnodedepth',
  MCESELECTNODE = 'mceselectnode',
  MCEINSERTCONTENT = 'mceinsertcontent',
  MCEINSERTRAWHTML = 'mceinsertrawhtml',
  MCEINSERTNEWLINE = 'mceinsertnewline',
  MCETOGGLEFORMAT = 'mcetoggleformat',
  MCESETCONTENT = 'mcesetcontent',
  INDENT = 'indent',
  OUTDENT = 'outdent',
  MCEREPAINT = 'mcerepaint',
  INSERTHORIZONTALRULE = 'inserthorizontalrule',
  MCETOGGLEVISUALAID = 'mcetogglevisualaid',
  MCEREPLACECONTENT = 'mcereplacecontent',
  MCEINSERTLINK = 'mceinsertlink',
  SELECTALL = 'selectall',
  MCENEWDOCUMENT = 'mcenewdocument',
  INSERTLINEBREAK = 'insertlinebreak',
  UNDO = 'undo',
  REDO = 'redo',
  TOGGLETOOLBARDRAWER = 'toggletoolbardrawer',
  MCENONBREAKING = 'mcenonbreaking',
  INSERTDEFINITIONLIST = 'insertdefinitionlist',
  REMOVELIST = 'removelist',
  MCELISTPROPS = 'mcelistprops',
  MCETOGGLEPLAINTEXTPASTE = 'mcetoggleplaintextpaste',
  MCEINSERTCLIPBOARDCONTENT = 'mceinsertclipboardcontent',
  MCEAPPLYTEXTCOLOR = 'mceapplytextcolor',
  MCEREMOVETEXTCOLOR = 'mceremovetextcolor',
  DELETE = 'delete',
  FORWARDDELETE = 'forwarddelete',
}

export type BaseEditorAutoCompleteSourceData = {
  id?: number;
  value: string;
  text?: string;
  icon?: string;
  modelName?: AutoSuggestModelName;
  preText?: string;
};

type BaseEditorAutoCompleteConfig = {
  /**
   * @description Character to trigger autocomplete (single ATM)
   */
  triggerChar: string;
  /**
   * @description Minimum characters after trigger character to launch autocomplete
   */
  minCharsAfterTrigger?: number;

  /**
   * @description Max sentences to send as context, use 0 for entire text, number for last N sentences
   */
  maxContextSentences?: number;

  /**
   * @description Source method returning the suggestion list for autocomplete.
   * @param context Context sentences received as autocomplete input (using above maxContextSentences extraction)
   * @param editor  Base editor component instance
   */
  source(
    context: string,
    suggestionPrefix: string,
    editor: IBaseEditor
  ): Promise<Array<BaseEditorAutoCompleteSourceData>>;

  /**
   * @description Predicate that launches autocomplete when true.
   * @default true
   * @param editor Base editor instance
   * @param pattern Input pattern after the trigger character
   */
  matches?(editor: IBaseEditor, pattern: string, text: string): boolean;

  /**
   *
   * @param editor Base editor instance
   * @param item Suggestion selected by user.
   */
  onSelect?(editor: IBaseEditor, item: BaseEditorAutoCompleteSourceData): void;
};

export interface Position {
  top?: number;
  left?: number;
  right?: number;
  bottom?: number;
}
export interface IBaseEditorProps {
  id: QuillEditorIDS;
  fixedToolbarContainerId?: string;
  mode: BaseEditorModes;
  maxPreContextSentences: number;
  maxPostContextSentences: number;
  sticktyToolbar?: boolean;
  placeholder: string;
  isSuggestionOpen: boolean;
  suggestionIndex: number;
  suggestionModelName: AutoSuggestModelName;
  suggestion: Suggestions;
  input: ProjectState;
  suggWidth: number;
  isMobile: boolean;
  isOpera: boolean;
  isMac: boolean;
  toolbarHeight?: number;
  projectStatus: ProjectStatusEnum;
  isCustomTooltipOpen: boolean;
  onboardingName: OnboardingName;
  menuType: SelectionEndMenuTypes;
  paintGrammarResponse: boolean;
  currentProjectId: string;
  setToolbarHeight?: React.Dispatch<React.SetStateAction<number>>;
  setTrashIcon: React.Dispatch<React.SetStateAction<boolean>>;
  onChange: () => void;
  updateAnalyticsData: (numberOfWords: number) => void;
  toggleAutoSuggest(toggle: boolean, index?: number, modelName?: AutoSuggestModelName): void;
  onIndexChange(index: number): void;
  setGrammarError(numberOfErrors: number): void;
  onAutosuggestTrigger(htmlElement: HTMLElement): void;
  height: number | string;
  onSelectionEnd(selectionObj: {
    text: string;
    contextEvent: React.PointerEvent<HTMLDivElement> | null;
    selectionEndMenu: SelectionEndMenuTypes;
  }): void;
  quillActionOptionPosition?: Position | null;
  onLoad: (editorId: string) => void;
  isGrammarCheckRunning: boolean;
  switchToCitationTab: () => void;
  setGrammarCheckRunning: (isGrammarCheckRunning: boolean) => void;
  setMarkerPostionToAddCitation: (postion: number) => void;
  toggleGrammarCheckFixAllButtonState: (isFixAllGrammarDisabled: boolean) => void;
  closeCustomTooltip: (arg: { open: boolean; message: string }) => void;
  onExportProject: () => void;
  onCopyProject: () => void;
}

// export interface EditorProps extends IBaseEditorProps {
// suggestion: Suggestions,
// }

export type EditorState = {
  replacementObject: IReplacementObject;
  anchorEl: HTMLElement | null;
  sentence: string;
  errorNodeHoverState: boolean;
  cardHoverState: boolean;
};
